import React from "react";
import { useNavigate } from "react-router-dom";
import AuthFormLayout from "components/auth/AuthFormLayout";
import InputField from "components/fields/InputField";
import { useLanguage } from "context/LanguageContext";
import { useInAppNotifications } from "context/InAppNotificationContext";
import { authService } from "services/authService";
import { triggerFCMRegistration } from "hooks/useFCM";
import { decodeJwt, toAppRole, roleHomePath } from "utils/auth";

interface LoginError {
  message: string;
  detail?: string;
}

export default function SignIn() {
  const { t } = useLanguage();
  const { refresh } = useInAppNotifications();
  const navigate = useNavigate();

  const [identifier, setIdentifier] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [loginError, setLoginError] = React.useState<LoginError | null>(null);

  const normalizeLoginError = React.useCallback(
    (err: unknown): LoginError => {
      if (
        err instanceof TypeError &&
        err.message.toLowerCase().includes("failed to fetch")
      ) {
        return { message: t("auth.networkError") };
      }
      const rawMessage = err instanceof Error ? err.message : String(err);
      const normalizedMessage = rawMessage.trim().toLowerCase();
      if (
        normalizedMessage.includes("invalid credentials") ||
        normalizedMessage.includes("incorrect username or password")
      ) {
        return { message: t("auth.invalidCredentials") };
      }
      return { message: rawMessage ? t(rawMessage) : t("auth.loginError") };
    },
    [t]
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError(null);
    setLoading(true);
    try {
      const tokens = await authService.login(identifier, password);
      sessionStorage.setItem("access_token", tokens.access_token);
      localStorage.setItem("refresh_token", tokens.refresh_token);
      triggerFCMRegistration();
      await authService.getMe().catch(() => {});
      if (tokens.is_first_login) {
        navigate("/first-login");
        return;
      }
      const payload = decodeJwt(tokens.access_token);
      const appRole = toAppRole((payload?.role as string) ?? "");
      navigate(roleHomePath(appRole));
      refresh();
    } catch (err: unknown) {
      setLoginError(normalizeLoginError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthFormLayout title={t("auth.signIn")} subtitle={t("signIn.subtitle")}>
      <form onSubmit={handleSubmit}>
        {loginError ? (
          <div
            role="alert"
            className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-500/15 dark:text-red-300 dark:border-red-500/30"
          >
            <p className="font-medium">{loginError.message}</p>
            {loginError.detail ? (
              <p className="mt-1 break-all font-mono text-xs text-red-500">
                {loginError.detail}
              </p>
            ) : null}
          </div>
        ) : null}
        <InputField
          variant="auth"
          extra="mb-3"
          label={t("signIn.identifierLabel")}
          placeholder={t("signIn.identifierPlaceholder")}
          id="identifier"
          type="text"
          value={identifier}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            if (loginError) setLoginError(null);
            setIdentifier(e.target.value);
          }}
        />
        <InputField
          variant="auth"
          extra="mb-3"
          label={t("auth.password")}
          placeholder={t("auth.passwordPlaceholder")}
          id="password"
          type="password"
          value={password}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            if (loginError) setLoginError(null);
            setPassword(e.target.value);
          }}
        />

        <button
          type="submit"
          disabled={loading}
          className="linear mt-2 w-full rounded-2xl bg-brand-500 py-[14px] text-base font-semibold text-white transition duration-200 hover:bg-brand-600 active:bg-brand-700 disabled:opacity-60"
        >
          {loading ? t("auth.signingIn") : t("auth.signIn")}
        </button>
      </form>
    </AuthFormLayout>
  );
}
